'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';

const CRUMB_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  'add-case': 'Add case',
  calendar: 'Calendar',
  referrals: 'Refer & Earn',
  settings: 'Settings',
  labels: 'Labels',
  case: 'Case',
  admin: 'Admin',
};

function labelFor(segment: string): string {
  if (CRUMB_LABELS[segment]) return CRUMB_LABELS[segment];
  return segment
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface TopbarProps {
  onOpenCmd: () => void;
}

export default function Topbar({ onOpenCmd }: TopbarProps) {
  const pathname = usePathname();

  const crumbs = useMemo(() => {
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length === 0) return [{ label: 'Workspace', href: '/dashboard' }];
    const acc: { label: string; href: string }[] = [{ label: 'Workspace', href: '/dashboard' }];
    let href = '';
    for (const p of parts) {
      href += '/' + p;
      acc.push({ label: labelFor(p), href });
    }
    return acc;
  }, [pathname]);

  return (
    <div className="ics-topbar">
      <nav aria-label="Breadcrumb" className="crumbs">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <span key={i} className="inline-flex items-center gap-2">
              {i > 0 && <span className="sep" aria-hidden>/</span>}
              {last ? (
                <span className="now">{c.label}</span>
              ) : (
                <Link href={c.href} className="hover:text-gray-900 transition-colors">
                  {c.label}
                </Link>
              )}
            </span>
          );
        })}
      </nav>

      <div className="top-actions">
        <button
          type="button"
          className="ics-btn-ghost inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[12.5px] text-gray-700 hover:bg-gray-100 border border-gray-200"
          onClick={onOpenCmd}
          title="Open command palette (⌘K)"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 6a3 3 0 10-3 3h3zM9 6v12M9 18a3 3 0 103-3v3zM15 18a3 3 0 103-3h-3zM15 18V6M15 6a3 3 0 10-3 3h3z" />
          </svg>
          <span className="hidden sm:inline">Ask or jump to…</span>
          <kbd className="hidden md:inline-block font-mono text-[10px] px-1.5 py-0.5 border border-gray-200 rounded bg-white text-gray-500">⌘K</kbd>
        </button>
      </div>
    </div>
  );
}
